import AiCard from "@/components/AiCard";
import Button from "@/components/Button";
import Tag from "@/components/Tag";
import type { TopicAnalysis } from "../index";

type TopicQualityPanelProps = {
  analysis: TopicAnalysis | null;
  analyzing: boolean;
  isDirty: boolean;
  onAnalyze: () => void;
};

function findingLabel(topics: string[], subtopics: string[]): string {
  const topicPart = topics.join(" / ");
  if (subtopics.length === 0) return topicPart;
  return `${topicPart} — ${subtopics.join(", ")}`;
}

export default function TopicQualityPanel({ analysis, analyzing, isDirty, onAnalyze }: TopicQualityPanelProps) {
  if (!analysis && !analyzing) {
    return (
      <div
        data-testid="topic-quality-empty"
        className="flex flex-col gap-6 rounded-lg bg-sys-brand-surface-container-low p-4"
      >
        <div className="flex flex-col gap-2">
          <h2 className="text-title-medium text-sys-brand-on-surface">Topic clarity review</h2>
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            AI checks this department&apos;s topics and subtopics for confusing labels, overlaps, and missing
            descriptions — the same context the call classifier uses. Run it after renaming or adding topics.
          </p>
        </div>
        <div>
          <Button variant="outlined" icon="psychology" onClick={onAnalyze} testId="analyze-topics">
            Review topics with AI
          </Button>
        </div>
      </div>
    );
  }

  if (analyzing && !analysis) {
    return (
      <div data-testid="topic-quality-loading">
        <AiCard title="Topic clarity review" text="Analyzing topics..." loading />
      </div>
    );
  }

  if (!analysis || !analysis.configured) {
    return (
      <div
        data-testid="topic-quality-unavailable"
        className="flex flex-col gap-6 rounded-lg bg-sys-brand-surface-container-low p-4"
      >
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          AI review isn&apos;t available right now. You can still edit topics and descriptions manually.
        </p>
        <div>
          <Button variant="outlined" onClick={onAnalyze} testId="analyze-topics-retry">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="topic-quality-results" className="flex flex-col gap-6">
      <AiCard title="Topic clarity review" text={analysis.summary || "Review complete."} />
      {isDirty ? (
        <p data-testid="topic-quality-stale" className="text-body-small text-sys-brand-on-surface-variant">
          Topics changed since this review — run it again for fresh feedback.
        </p>
      ) : null}
      {analysis.findings.length === 0 ? (
        <p data-testid="topic-quality-clear" className="text-body-medium text-sys-brand-on-surface-variant">
          No confusing topics found. Clear labels and descriptions help the classifier route calls correctly.
        </p>
      ) : (
        <ul data-testid="topic-quality-findings" className="flex flex-col gap-3">
          {analysis.findings.map((finding, index) => (
            <li
              key={`${finding.topics.join("|")}|${finding.subtopics.join("|")}|${finding.message}|${index}`}
              data-testid={`topic-quality-finding-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Tag tagType={finding.severity === "warning" ? "warn" : "information"} size="small">
                  {finding.severity === "warning" ? "Needs attention" : "Suggestion"}
                </Tag>
                <span className="text-body-medium font-medium text-sys-brand-on-surface">
                  {findingLabel(finding.topics, finding.subtopics)}
                </span>
              </div>
              <p className="text-body-medium text-sys-brand-on-surface-variant">{finding.message}</p>
              {finding.suggestion ? (
                <p className="text-body-medium text-sys-brand-on-surface">
                  <span className="font-medium">Suggested fix: </span>
                  {finding.suggestion}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button
          variant="outlined"
          icon="psychology"
          loading={analyzing}
          onClick={onAnalyze}
          testId="analyze-topics-rerun"
        >
          Run review again
        </Button>
      </div>
    </div>
  );
}
